// The running-total view of the growth charts (spGrowth in sound-page.js, dpChart in detail-page.js). We don't read
// every sound or profile every day, so the line is drawn only through real readings: straight segments across the
// days without one, a soft fill under it and a small dot on each reading. Milestone labels sit beside their dot on
// the side the line isn't (a running total rises left to right, so above-left, else below-right).

const fmtDay = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const nf = (n) => Math.round(n).toLocaleString('en-US');
const short = (n) => {
    const a = Math.abs(n);
    if (a >= 1e6) return `${(n / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(/\.0$/, '')}M`;
    if (a >= 1e3) return `${(n / 1e3).toFixed(a >= 1e4 ? 0 : 1).replace(/\.0$/, '')}K`;
    return `${Math.round(n)}`;
};
const DAY = 864e5;

/**
 * SVG for the line, its fill, the reading dots and the milestones. P: one point per day ({d, total}); px/py map a
 * day index and a value to pixels. Returns the markup and each day's y (null on days without a reading).
 */
export function drawTotal({ P, w, top, bot, px, py, milestones = [] }) {
    const K = P.map((p, i) => (p.total == null ? null : i)).filter((i) => i !== null);
    if (!K.length) return { body: '', ys: P.map(() => null) };
    const pts = K.map((i) => [px(i), py(P[i].total)]);
    const line = pts.map(([x, y], j) => `${j ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join('');
    let body = `<path d="${line}L${pts.at(-1)[0].toFixed(1)},${bot}L${pts[0][0].toFixed(1)},${bot}Z" class="sp-g-area"/>`;
    body += `<path d="${line}" class="sp-g-line" vector-effect="non-scaling-stroke"/>`;
    // Reading dots only while they stay apart; on a dense 60-day line they would merge into a second stroke.
    const gap = pts.length > 1 ? (pts.at(-1)[0] - pts[0][0]) / (pts.length - 1) : Infinity;
    if (gap >= 16) body += pts.slice(0, -1).map(([x, y]) => `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2.5" class="sp-g-pt"/>`).join('');

    // The line's height at x, between readings.
    const lineY = (x) => {
        if (x <= pts[0][0]) return pts[0][1];
        for (let j = 1; j < pts.length; j++) {
            const [x1, y1] = pts[j];
            if (x <= x1) { const [x0, y0] = pts[j - 1]; return y0 + ((y1 - y0) * (x - x0)) / Math.max(1e-6, x1 - x0); }
        }
        return pts.at(-1)[1];
    };
    // A label box [x0, x1] × [y0, y1] is clear when it stays in the plot and the line passes outside it.
    const clear = (x0, x1, y0, y1) => {
        if (x0 < 2 || x1 > w - 2 || y0 < 0 || y1 > bot - 2) return false;
        for (let x = x0; x <= x1; x += 4) { const y = lineY(x); if (y > y0 - 4 && y < y1 + 4) return false; }
        return true;
    };
    const taken = [];
    const free = (b) => !taken.some((t) => b[0] < t[1] + 8 && b[1] > t[0] - 8 && b[2] < t[3] + 4 && b[3] > t[2] - 4);
    // Newest milestones first: they matter most when there is room for only some labels.
    [...milestones].reverse().forEach((m) => {
        const i = P.findIndex((p) => p.d === m.date);
        if (i < 0) return;
        const x = px(i);
        const y = py(P[i].total ?? m.videos);
        body += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4" class="sp-g-dot sp-hl"/>`;
        const text = `${m.label} · ${fmtDay(m.date)}`;
        const tw = text.length * 6.4;
        const spots = [
            [x - 8 - tw, x - 8, y - 22, y - 8, 'end', x - 8, y - 11],
            [x + 8, x + 8 + tw, y + 8, y + 22, 'start', x + 8, y + 19],
            [x - tw / 2, x + tw / 2, y - 30, y - 16, 'middle', x, y - 19],
        ];
        const spot = spots.find(([x0, x1, y0, y1]) => clear(x0, x1, y0, y1) && free([x0, x1, y0, y1]));
        if (!spot) return;
        taken.push(spot.slice(0, 4));
        body += `<text x="${spot[5].toFixed(1)}" y="${spot[6].toFixed(1)}" text-anchor="${spot[4]}" class="sp-g-ml">${text}</text>`;
    });
    body += `<circle cx="${pts.at(-1)[0].toFixed(1)}" cy="${pts.at(-1)[1].toFixed(1)}" r="4.5" class="sp-g-dot"/>`;

    return { body, ys: P.map((p) => (p.total == null ? null : py(p.total))) };
}

/** The day with a reading closest to day i (i itself when it has one). */
export function nearestReading(P, i) {
    let best = null;
    P.forEach((p, j) => { if (p.total != null && (best === null || Math.abs(j - i) < Math.abs(best - i))) best = j; });
    return best ?? i;
}

/**
 * Change since the reading before day i: { label: 'Since 13 Oct', value: '+48,210', rate: '+12K' } (the average
 * per day, only when the readings are more than a day apart), or null for the first reading or a day without one.
 * With floor, a drop counts as no change: totals that only grow (videos on a sound) dip when TikTok rounds them.
 */
export function sinceLast(P, i, { floor = false } = {}) {
    const p = P[i];
    if (p?.total == null) return null;
    let j = i - 1;
    while (j >= 0 && P[j].total == null) j--;
    if (j < 0) return null;
    const diff = floor ? Math.max(0, p.total - P[j].total) : p.total - P[j].total;
    const days = Math.round((Date.parse(`${p.d}T00:00:00Z`) - Date.parse(`${P[j].d}T00:00:00Z`)) / DAY);
    return {
        label: days === 1 ? 'Since the day before' : `Since ${fmtDay(P[j].d)}`,
        value: `${diff < 0 ? '−' : '+'}${nf(Math.abs(diff))}`,
        rate: days > 1 ? `${diff < 0 ? '−' : '+'}${short(Math.abs(diff / days))}` : '',
        up: diff >= 0,
    };
}
